import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { TableModule } from "primeng/table";
import { FormsModule } from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { DatePickerModule } from "primeng/datepicker";
import { CommonModule } from "@angular/common";
import { DateFilter } from "../../../core/models/common/date-filter-request.dto";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";

@Component({
  selector: "jni-date-filter",
  templateUrl: "./date-filter.component.html",
  styleUrls: ["./date-filter.component.scss"],
  imports: [
    TableModule,
    SelectModule,
    FormsModule,
    DialogModule,
    DatePickerModule,
    CommonModule,
    ButtonModule,
  ],
})
/* eslint-disable @typescript-eslint/no-explicit-any */
export class DateFilterComponent<T extends Record<string, any>>
  implements OnInit
{
  /* eslint-enable @typescript-eslint/no-explicit-any */
  dateFilter = {} as DateFilter;
  matchModeOptions = [
    { label: "Between", value: "between" },
    { label: "Date is", value: "dateIs" },
    { label: "Date before", value: "dateBefore" },
    { label: "Date after", value: "dateAfter" },
  ];

  showFilterDialog = false;
  filterList: T[] = [];
  isError = false;
  constructor(private _toastr: JniToastrService) {}

  @Input() columnName = "";
  @Input() dataList: T[] = [];
  @Input() filteredDataList: T[] = [];
  @Input() filterMode = "client";
  @Output() emitApplyFilter: EventEmitter<T[] | DateFilter> = new EventEmitter<
    T[] | DateFilter
  >();
  @Output() emitCancelFilter: EventEmitter<T[] | DateFilter> = new EventEmitter<
    T[] | DateFilter
  >();

  ngOnInit() {
    this.dateFilter.startDate =
      this.dateFilter.endDate =
      this.dateFilter.selectedMatchMode =
        "";
  }

  convertStringToDateExcludeTime(stringDate: string) {
    if (stringDate) {
      return new Date(stringDate).setHours(0, 0, 0, 0);
    } else return new Date();
  }

  applyFilter() {
    if (this.filterMode === "client") {
      this.clientSideApplyFilter();
    } else if (this.filterMode === "server") {
      this.serverSideApplyFilter();
    }
  }

  clearFilter() {
    if (this.filterMode === "client") {
      this.clientSideClearFilter();
    } else if (this.filterMode === "server") {
      this.serverSideClearFilter();
    }
  }

  private clientSideApplyFilter() {
    if (
      !this.dateFilter.selectedMatchMode ||
      this.dateFilter.selectedMatchMode === ""
    ) {
      this.isError = true;
      this._toastr.error("Error", "Select a Match Mode");
      return;
    }

    if (
      this.dateFilter.selectedMatchMode === "between" &&
      this.dateFilter.startDate &&
      this.dateFilter.endDate &&
      this.dateFilter.startDate > this.dateFilter.endDate
    ) {
      this.isError = true;
      this._toastr.error("Error", "Start date must be less than end date");
      return;
    }

    this.filterList =
      this.filteredDataList.length != 0 ? this.filteredDataList : this.dataList;
    switch (this.dateFilter.selectedMatchMode) {
      case "dateIs":
        this.filterList = this.filterList.filter(x =>
          x[`${this.columnName}`]
            ? this.convertStringToDateExcludeTime(x[this.columnName]) ==
              this.convertStringToDateExcludeTime(this.dateFilter.startDate)
            : null
        );
        break;
      case "dateIsNot":
        this.filterList = this.filterList.filter(x =>
          x[this.columnName]
            ? this.convertStringToDateExcludeTime(x[this.columnName]) !==
              this.convertStringToDateExcludeTime(this.dateFilter.startDate)
            : null
        );
        break;
      case "dateBefore":
        this.filterList = this.filterList.filter(x =>
          x[this.columnName]
            ? this.convertStringToDateExcludeTime(x[this.columnName]) <
              this.convertStringToDateExcludeTime(this.dateFilter.startDate)
            : null
        );
        break;
      case "dateAfter":
        this.filterList = this.filterList.filter(x =>
          x[this.columnName]
            ? this.convertStringToDateExcludeTime(x[this.columnName]) >
              this.convertStringToDateExcludeTime(this.dateFilter.startDate)
            : null
        );
        break;
      case "between":
        if (this.dateFilter.startDate && this.dateFilter.endDate) {
          this.filterList = this.filterList.filter(x => {
            const date = x[this.columnName]
              ? this.convertStringToDateExcludeTime(x[this.columnName] ?? "")
              : null;
            if (date !== null) {
              return (
                date >=
                  this.convertStringToDateExcludeTime(
                    this.dateFilter.startDate
                  ) &&
                date <=
                  this.convertStringToDateExcludeTime(this.dateFilter.endDate)
              );
            }
            return false;
          });
        } else if (this.dateFilter.startDate && !this.dateFilter.endDate) {
          this.filterList = this.filterList.filter(x => {
            const date = x[this.columnName]
              ? this.convertStringToDateExcludeTime(x[this.columnName] ?? "")
              : null;
            if (date !== null) {
              return (
                date >=
                  this.convertStringToDateExcludeTime(
                    this.dateFilter.startDate
                  ) &&
                date <=
                  this.convertStringToDateExcludeTime(new Date().toString())
              );
            }
            return false;
          });
        }
        break;
    }

    this.filteredDataList = this.filterList;
    this.showFilterDialog = false;
    this.emitApplyFilter.emit(this.filteredDataList);
  }

  private clientSideClearFilter() {
    this.dateFilter = {} as DateFilter;
    this.dateFilter.startDate =
      this.dateFilter.endDate =
      this.dateFilter.selectedMatchMode =
        "";
    this.showFilterDialog = false;
    this.emitCancelFilter.emit(this.dataList);
  }

  private serverSideApplyFilter() {
    if (
      !this.dateFilter.selectedMatchMode ||
      this.dateFilter.selectedMatchMode === ""
    ) {
      this.isError = true;
      this._toastr.error("Error", "Select a Match Mode");
      return;
    }

    if (
      this.dateFilter.selectedMatchMode === "between" &&
      this.dateFilter.startDate &&
      this.dateFilter.endDate &&
      this.dateFilter.startDate > this.dateFilter.endDate
    ) {
      this.isError = true;
      this._toastr.error("Error", "Start date must be less than end date");
      return;
    }
    this.dateFilter.columnName = this.columnName;
    this.showFilterDialog = false;
    this.emitApplyFilter.emit({ ...this.dateFilter });
  }

  private serverSideClearFilter() {
    this.showFilterDialog = false;
    this.dateFilter = {} as DateFilter;
    this.dateFilter.columnName = this.columnName;
    this.emitCancelFilter.emit(this.dateFilter);
  }
}
